/**
 * Theme tokens, mapped onto the Intelliport design system.
 *
 * The palette itself lives in styles/intelliport/tokens/colors.css — this file
 * only names the roles the app uses and points them at those variables. Keeping
 * the exported keys stable (bgCard, textPrimary, gold, …) is what let the whole
 * app change colour without touching a single component: every consumer already
 * reads through these names.
 *
 * Values are CSS custom-property references rather than literals so they resolve
 * dynamically when applyTheme() swaps the dark overrides on <html>.
 */
export const theme = {
  // Backgrounds
  bgBase:    "var(--bg)",
  bgCard:    "var(--surface)",
  bgNav:     "var(--surface)",
  bgInput:   "var(--surface-sunken)",

  // Borders
  border:      "var(--border)",
  borderStrong: "var(--border-strong)",
  borderHover: "var(--accent)",

  // Brand / accent. `gold` is the fill (Pantone 465 C); `goldText` is the
  // darker step that stays legible as text on a light surface, and is what the
  // dark palette re-points upward. Using the fill colour for text is the most
  // common way to fail contrast against white, so the two are kept separate.
  gold:      "var(--accent)",
  goldText:  "var(--accent-text)",
  goldSoft:  "var(--accent-soft)",
  goldLight: "var(--gold-300)",
  goldDark:  "var(--gold-700)",
  // Text/icon sitting ON a fill rather than on the page: `onGold` for the brand
  // fill, `onDark` for any dark or coloured chip. Both stay fixed across themes
  // — the thing they sit on does not change with the palette.
  onGold:    "var(--on-accent)",
  onDark:    "var(--text-on-inverse)",

  // Text
  textPrimary:   "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted:     "var(--text-muted)",

  // Status — semantic, deliberately separate from the accent so "warning" never
  // reads as "branded".
  danger:      "var(--status-offline)",
  dangerSoft:  "var(--red-50)",
  warning:     "var(--status-warning)",
  warningSoft: "var(--amber-50)",
  success:     "var(--status-online)",
  successSoft: "var(--green-50)",
  info:        "var(--status-info)",
  infoSoft:    "var(--blue-50)",

  // Focus
  focusRing: "var(--focus-ring)",

  // Type
  fontSans:      "var(--font-sans)",
  fontCondensed: "var(--font-condensed)",
  fontMono:      "var(--font-mono)",

  // Misc
  radius:       "6px",
  fontSizeBase: "var(--text-body)",
  fontSizeSm:   "var(--text-sm)",
  fontSizeXs:   "var(--text-xs)",
  fontSize2Xs:  "var(--text-2xs)",
} as const;

// ── Dark palette ──────────────────────────────────────────────────────────────
// The brand system is authored light-only, so these are the dark counterparts
// carried over from the UI design: the ink (#1A1C1E / Pantone 426 C) becomes the
// ground, surfaces step up from it rather than down, and borders become
// translucent white so they read at every elevation. The status hues are
// lightened — the light-mode versions are muted enough that they disappear
// against a dark ground — and the gold is used only in its lighter step, since
// the brand fill is too dark to sit as text on ink.
const DARK_VARS: Record<string, string> = {
  "--bg":              "#1A1C1E",
  "--surface":         "#25282A",
  "--surface-sunken":  "#1F2224",

  "--text-primary":    "#F3F2F0",
  "--text-secondary":  "#B9B7B5",
  "--text-muted":      "#8A8886",

  "--border":          "rgba(255,255,255,0.12)",
  "--border-strong":   "rgba(255,255,255,0.24)",

  "--accent-text":     "#E3C684",
  "--accent-soft":     "rgba(216,176,98,0.16)",
  "--gold-300":        "rgba(216,176,98,0.5)",

  "--gray-50":         "#2C2F31",
  "--gray-100":        "#2C2F31",
  "--gray-400":        "#5F5C5E",

  // Status: brighter hues, and the "soft" fills become low-alpha tints of the
  // hue itself instead of near-white pastels.
  "--status-online":   "#58B274",
  "--status-warning":  "#E8B65A",
  "--status-offline":  "#D96A5E",
  "--status-info":     "#6E9BB8",
  "--green-500":       "#58B274",
  "--amber-500":       "#E8B65A",
  "--red-500":         "#D96A5E",
  "--blue-500":        "#6E9BB8",
  "--green-50":        "rgba(63,157,90,0.16)",
  "--amber-50":        "rgba(224,161,46,0.16)",
  "--red-50":          "rgba(199,73,59,0.18)",
  "--blue-50":         "rgba(62,110,142,0.20)",
};

/**
 * A translucent tint of any theme colour.
 *
 * Every colour here is a CSS custom property, and you cannot append an alpha
 * suffix to one: `var(--status-online)1A` is not a colour, so the whole
 * declaration is silently dropped. That idiom was valid while the palette was
 * literal hex and it broke, invisibly, the moment the tokens moved to
 * variables — tinted pill backgrounds and coloured borders simply stopped
 * rendering, with no console error and no type error to catch it.
 *
 * color-mix() composes with variables, which is the whole point.
 *
 * @param color   any token from `theme`
 * @param percent opacity as a percentage, 0-100
 */
export function tint(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

export type ThemeMode = "light" | "dark";

/**
 * Apply a palette by overriding CSS custom properties on <html>.
 *
 * Light mode *removes* the overrides rather than setting a second table, so the
 * design system's own tokens stay the single source of truth and cannot drift
 * from a duplicated copy here.
 */
export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(DARK_VARS)) {
    if (mode === "dark") root.style.setProperty(key, value);
    else root.style.removeProperty(key);
  }
  root.dataset.theme = mode;
}
